/**
 * CuisineCommunePage — page de destination SEO longue traîne.
 *
 * Route : /livraison/:cuisine/:commune
 * Ex.   : /livraison/pizza/ixelles     → "Livraison Pizza à Ixelles — Nold Go"
 *
 * Chaque page est indexable, contient un H1 unique, une méta description
 * unique, un JSON-LD `ItemList` avec les restaurants filtrés, et un contenu
 * éditorial court. Butoir : si aucun restaurant ne matche, on affiche
 * quand même la page (canonical propre) avec un CTA vers /restaurants.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { UtensilsCrossed, MapPin, Bike, Star, ArrowRight, Loader2 } from "lucide-react";
import api from "../lib/api";
import { mediaUrl } from "../lib/mediaUrl";
import SEOHead from "../components/SEOHead";
import OpenFastBadge from "../components/OpenFastBadge";
import { isOpenNow } from "../lib/openingHours";

// Table canonique : chaque cuisine a un slug URL + libellé + liste de mots-clés
// utilisés pour matcher le champ `r.cuisine` (regex insensible casse).
const CUISINES = {
  pizza:      { label: "Pizza",       kws: ["pizza", "napolitaine"] },
  burger:     { label: "Burger",      kws: ["burger", "smash", "fast-food"] },
  sushi:      { label: "Sushi",       kws: ["sushi", "japonais", "japonaise"] },
  italien:    { label: "Italien",     kws: ["italien", "italienne", "pasta", "pâte", "trattoria"] },
  asiatique:  { label: "Asiatique",   kws: ["asiat", "thai", "thaï", "viet", "chinois"] },
  africain:   { label: "Africain",    kws: ["africain", "africaine", "cameroun", "sénégal", "senegal", "maroc", "congolais", "ivoirien"] },
  vegetarien: { label: "Végétarien",  kws: ["végé", "vege", "vegan", "veggie", "sans-viande"] },
  brasserie:  { label: "Brasserie",   kws: ["brasserie", "belge", "belgique"] },
};

const COMMUNES = {
  bruxelles:              "Bruxelles",
  ixelles:                "Ixelles",
  "saint-gilles":         "Saint-Gilles",
  etterbeek:              "Etterbeek",
  uccle:                  "Uccle",
  anderlecht:             "Anderlecht",
  schaerbeek:             "Schaerbeek",
  forest:                 "Forest",
  "woluwe-saint-lambert": "Woluwe-Saint-Lambert",
  "woluwe-saint-pierre":  "Woluwe-Saint-Pierre",
};

function matchesCuisine(resto, kws) {
  const c = (resto?.cuisine || "").toLowerCase();
  return kws.some((kw) => c.includes(kw));
}

function matchesCommune(resto, communeLabel) {
  const rc = (resto?.commune || resto?.city || "").toLowerCase();
  return rc.includes(communeLabel.toLowerCase());
}

export default function CuisineCommunePage() {
  const { cuisine, commune } = useParams();
  const cuisineDef = CUISINES[cuisine];
  const communeLabel = COMMUNES[commune];

  const [restos, setRestos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cuisineDef || !communeLabel) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        // On tente d'abord un filtre serveur (q=cuisine + city=commune),
        // puis on affine côté client sur toute la liste.
        const r = await api.get(`/restaurants?limit=100&q=${encodeURIComponent(cuisineDef.label)}&city=${encodeURIComponent(communeLabel)}`);
        const items = Array.isArray(r.data) ? r.data : (r.data.items || []);
        const filtered = items.filter((x) => matchesCuisine(x, cuisineDef.kws) && matchesCommune(x, communeLabel));
        if (alive) setRestos(filtered);
      } catch { /* ignore */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [cuisine, commune, cuisineDef, communeLabel]);

  // ⚠️ IMPORTANT : tous les hooks doivent être appelés AVANT le early return
  // sinon React casse ("rules-of-hooks"). On calcule le JSON-LD même si
  // cuisine/commune inconnus (il ne sera pas rendu — early return juste après).
  const itemListLd = useMemo(() => {
    if (!cuisineDef || !communeLabel) return null;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      name: `Restaurants ${cuisineDef.label} à ${communeLabel}`,
      itemListElement: restos.slice(0, 20).map((r, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Restaurant",
          "@id": `https://www.noldgo.com/restaurants/${r.id}`,
          name: r.name,
          image: r.cover_url || undefined,
          servesCuisine: r.cuisine || cuisineDef.label,
          address: {
            "@type": "PostalAddress",
            addressLocality: r.commune || communeLabel,
            addressCountry: "BE",
          },
          url: `https://www.noldgo.com/restaurants/${r.id}`,
        },
      })),
    };
  }, [restos, cuisineDef, communeLabel]);

  // Fallback : cuisine ou commune inconnues → SEO 404 propre.
  if (!cuisineDef || !communeLabel) {
    return (
      <div className="min-h-screen bg-[#FDF8F0] flex items-center justify-center p-6" data-testid="cuisine-commune-404">
        <SEOHead
          title="Page introuvable — Nold Go"
          description="Cette page n'existe pas. Découvrez tous nos restaurants partenaires à Bruxelles sur Nold Go."
          noindex
        />
        <div className="text-center max-w-md">
          <h1 className="font-head text-3xl font-black text-[#1A1A1A]">Page introuvable</h1>
          <p className="mt-3 text-[#75807A]">Découvrez tous nos restaurants partenaires à Bruxelles.</p>
          <Link to="/restaurants" className="inline-flex items-center gap-2 mt-6 px-5 py-2.5 rounded-full bg-[#059669] text-white font-bold hover:bg-[#047857] transition-colors">
            Voir tous les restaurants <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const title = `Livraison ${cuisineDef.label.toLowerCase()} à ${communeLabel} — Nold Go`;
  const description = `Commandez ${cuisineDef.label.toLowerCase()} à ${communeLabel} et faites-vous livrer à vélo en 30 minutes. Restaurants partenaires vérifiés · Prix transparents · Paiement carte sécurisé. Nold Go, la livraison de repas belge.`;

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="cuisine-commune-page">
      <SEOHead
        title={title}
        description={description}
        canonical={`/livraison/${cuisine}/${commune}`}
        type="website"
        jsonLd={itemListLd}
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <nav className="text-xs text-[#75807A] mb-4">
          <Link to="/" className="hover:text-[#059669]">Accueil</Link>
          <span className="mx-1">›</span>
          <Link to="/restaurants" className="hover:text-[#059669]">Restaurants</Link>
          <span className="mx-1">›</span>
          <span className="text-[#1A1A1A] font-semibold">{cuisineDef.label} · {communeLabel}</span>
        </nav>

        <header className="mb-8">
          <p className="text-xs uppercase tracking-widest text-[#F26419] font-bold">Nold Go · {communeLabel}</p>
          <h1 className="font-head text-3xl sm:text-4xl lg:text-5xl font-black text-[#1A1A1A] mt-1">
            Livraison {cuisineDef.label.toLowerCase()} à {communeLabel}
          </h1>
          <p className="mt-3 text-[#3B423E] text-sm sm:text-base max-w-2xl">
            Découvrez les meilleurs restaurants {cuisineDef.label.toLowerCase()} à {communeLabel}
            {" "}livrés à vélo en moins de 30 minutes par les coursiers Nold Go. Paiement carte
            sécurisé, prix transparents, sans engagement.
          </p>
        </header>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-[#75807A]">
            <Loader2 className="w-6 h-6 animate-spin mr-2" /> Chargement des restaurants…
          </div>
        ) : restos.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-[#E2DFD8] p-8 text-center">
            <UtensilsCrossed className="w-10 h-10 mx-auto text-[#75807A]" />
            <p className="mt-3 text-[#3B423E] font-semibold">
              Aucun restaurant {cuisineDef.label.toLowerCase()} livrant à {communeLabel} pour le moment.
            </p>
            <p className="mt-1 text-sm text-[#75807A]">
              Nous ajoutons chaque semaine de nouveaux partenaires. En attendant :
            </p>
            <Link to="/restaurants" className="inline-flex items-center gap-2 mt-4 px-4 py-2 rounded-full bg-[#F26419] text-white text-sm font-bold hover:bg-[#D94F2B] transition-colors">
              Voir tous nos restaurants <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
            {restos.map((r) => (
              <Link key={r.id} to={`/restaurants/${r.id}`} data-testid={`cc-resto-${r.id}`}
                className="group bg-white rounded-2xl border border-[#E2DFD8] overflow-hidden hover:border-[#059669] hover:shadow-xl transition-all">
                <div className="relative aspect-[16/10] bg-[#F0EBE1] overflow-hidden">
                  {r.cover_url ? (
                    <img src={mediaUrl(r.cover_url)} alt={r.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      loading="lazy" width="400" height="250"
                      onError={(e) => { e.currentTarget.style.display = "none"; }} />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#75807A]"><UtensilsCrossed className="w-10 h-10" /></div>
                  )}
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#059669] text-white text-[10px] font-black uppercase tracking-wider shadow-md">
                    <Bike className="w-3 h-3" /> Livraison
                  </div>
                  <div className="absolute bottom-3 right-3">
                    <OpenFastBadge resto={r} />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-head font-bold text-[#1A1A1A] text-lg truncate">{r.name}</h3>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-[#75807A]">
                    <MapPin className="w-3.5 h-3.5 shrink-0" />
                    <span className="truncate">{r.commune || r.address}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between text-xs">
                    <span className="inline-flex items-center gap-1 text-[#059669] font-bold">
                      <Star className="w-3.5 h-3.5 fill-[#059669]" /> {isOpenNow(r.opening_hours_text) === true ? "Ouvert" : "Voir la carte"}
                    </span>
                    <span className="inline-flex items-center gap-1 font-bold text-[#F26419] group-hover:translate-x-1 transition-transform">
                      Commander <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}

        {/* Bloc SEO textuel — donne du contenu unique à la page. */}
        <section className="mt-10 prose prose-sm max-w-none text-[#3B423E]">
          <h2 className="font-head text-xl font-black text-[#1A1A1A]">Pourquoi commander {cuisineDef.label.toLowerCase()} à {communeLabel} sur Nold Go ?</h2>
          <p>
            Nold Go est le service belge de livraison de repas qui met en relation les restaurateurs
            de {communeLabel} avec les habitants et les visiteurs de la commune. En quelques clics,
            vous parcourez la carte de restaurants {cuisineDef.label.toLowerCase()} sélectionnés à {communeLabel},
            passez commande et êtes livrés à vélo, en moins de 30 minutes.
          </p>
          <p>
            Contrairement à d'autres plateformes, Nold Go verse <b>75 % du montant de la commande</b>
            {" "}directement au restaurateur — un modèle plus juste qui garantit la pérennité des
            commerces locaux de {communeLabel}. Nos livreurs sont salariés, formés et équipés pour
            que votre repas arrive chaud et intact.
          </p>
          <h3 className="font-head text-lg font-bold text-[#1A1A1A] mt-4">Zones de livraison à proximité</h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-sm">
            {Object.entries(COMMUNES)
              .filter(([slug]) => slug !== commune)
              .slice(0, 6)
              .map(([slug, label]) => (
                <li key={slug}>
                  <Link to={`/livraison/${cuisine}/${slug}`} className="text-[#059669] hover:underline">
                    Livraison {cuisineDef.label.toLowerCase()} à {label}
                  </Link>
                </li>
              ))}
          </ul>
          <h3 className="font-head text-lg font-bold text-[#1A1A1A] mt-4">Autres cuisines populaires à {communeLabel}</h3>
          <ul className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2 text-sm">
            {Object.entries(CUISINES)
              .filter(([slug]) => slug !== cuisine)
              .slice(0, 6)
              .map(([slug, def]) => (
                <li key={slug}>
                  <Link to={`/livraison/${slug}/${commune}`} className="text-[#F26419] hover:underline">
                    Livraison {def.label.toLowerCase()} à {communeLabel}
                  </Link>
                </li>
              ))}
          </ul>
        </section>
      </div>
    </div>
  );
}
